import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { itemCategories } from '../config/boothPricing';
import { getBusinessTypes } from '../services/lookupsService';
import { getUserProfile } from '../services/profilesService';
import { createBoothApplication, updateMyPendingApplication, getLatestApplicationForVendorEvent, updatePendingApplicationForVendor } from '../services/applicationsService';
import { getEvent } from '../services/eventsService';
import { getLayoutPublic } from '../services/layoutsService';
import { listVendorsForUser, listVendors } from '../services/vendorsService';
import './VendorApplication.css';

const emptyBooth = (firstLevelKey) => ({ level: firstLevelKey || '', position: 'INLINE', itemType: '', notes: '' });

const VendorApplication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [rows, setRows] = useState([emptyBooth()]);
  const [event, setEvent] = useState(location.state?.event || null);
  const [layout, setLayout] = useState(location.state?.layout || null);
  const [ack, setAck] = useState(false);
  const [packagedFoodAck, setPackagedFoodAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSubmittedDialog, setShowSubmittedDialog] = useState(false);
  const [bizTypes, setBizTypes] = useState([]);
  const [latest, setLatest] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [userType, setUserType] = useState('Vendor');

  useEffect(() => {
    (async () => {
      try { setBizTypes(await getBusinessTypes()); } catch {}
      // latest for current event once event is known
      if (!event && location.state?.event) {
        setEvent(location.state.event);
      }
      if (!layout && location.state?.layout) setLayout(location.state.layout);
      // Load profile (role + default vendor)
      try {
        if (currentUser?.uid) {
          const p = await getUserProfile(currentUser.uid);
          if (p) {
            setUserType(p.userType || 'Vendor');
            if (p.vendorId && !selectedVendorId) setSelectedVendorId(p.vendorId);
          }
        }
      } catch {}
      // Allow deep link via ?eventId=
      try {
        const search = new URLSearchParams(location.search);
        const eventId = search.get('eventId');
        if (!event && eventId) {
          const ev = await getEvent(eventId);
          if (ev) {
            setEvent(ev);
          }
          if (ev?.layoutId && !layout) {
            try { const ly = await getLayoutPublic(ev.layoutId); setLayout(ly); } catch {}
          }
        }
      } catch {}
    })();
  }, [location.state, location.search, currentUser, selectedVendorId]);

  // Once event is available, load vendor list; app is loaded based on selectedVendorId in a separate effect
  useEffect(() => {
    (async () => {
      if (!event?.id) return;
      // Load vendors according to role
      try {
        let list = [];
        if (userType === 'Admin' || userType === 'Organizer') {
          list = await listVendors();
        } else if (currentUser?.uid) {
          list = await listVendorsForUser(currentUser.uid);
        }
        setVendors(Array.isArray(list) ? list : []);
        // default vendor if still not selected
        if (!selectedVendorId && list.length === 1) setSelectedVendorId(list[0].id);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, userType]);

  // When a vendor is selected (or preselected), reflect that vendor's application for this event
  useEffect(() => {
    (async () => {
      if (!event?.id) return;
      if (!selectedVendorId) {
        setLatest(null);
        setRows([]);
        setAck(false);
        return;
      }
      try {
        const app = await getLatestApplicationForVendorEvent(selectedVendorId, event.id);
        setLatest(app);
        if (app) {
          const appRows = (app.boothsRequested || []).map(b => ({
            level: b.level || firstLevelKey,
            position: b.position || 'INLINE',
            itemType: b.itemType || '',
            notes: b.notes || ''
          }));
          setRows(appRows);
          setAck(!!app.acknowledgement);
          // If loading a pending application with packaged food booths, set packagedFoodAck to true
          const hasPackagedFoodInApp = appRows.some(r => (r.itemType || '').trim() === 'Packaged Food');
          if (hasPackagedFoodInApp) {
            setPackagedFoodAck(true);
          }
        } else {
          setRows([]); // show no booths entered yet
          setAck(false);
        }
      } catch (e) {
        setRows([]);
        setAck(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendorId, event?.id]);

  const sortedLevels = useMemo(() => {
    const entries = Object.entries(layout?.customLevels || {});
    // Sort by most expensive price (max of inline/corner) descending
    return entries.sort((a, b) => {
      const aMax = Math.max(Number(a[1]?.inlinePrice || 0), Number(a[1]?.cornerPrice || 0));
      const bMax = Math.max(Number(b[1]?.inlinePrice || 0), Number(b[1]?.cornerPrice || 0));
      return bMax - aMax;
    });
  }, [layout]);
  const firstLevelKey = sortedLevels.length ? sortedLevels[0][0] : '';

  useEffect(() => {
    if (layout && rows.length === 1 && !rows[0].level) {
      setRows([emptyBooth(firstLevelKey)]);
    }
  }, [layout]);

  const itemTypeOptions = useMemo(() => bizTypes?.length ? bizTypes : itemCategories, [bizTypes]);

  // `layout.customLevels` stores prices in cents. Format as dollars for UI.
  const fmtCurrency = (n) => {
    const cents = Number(n || 0);
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(dollars);
  };

  const addRow = () => setRows(prev => [...prev, emptyBooth(firstLevelKey)]);
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) => {
    // Check if itemType is being changed to "Packaged Food"
    if (patch.itemType) {
      const newItemType = (patch.itemType || '').trim();
      const oldItemType = (rows[idx]?.itemType || '').trim();
      const isPackagedFood = newItemType === 'Packaged Food';
      const wasPackagedFood = oldItemType === 'Packaged Food';
      
      // Only show popup if switching TO packaged food (not already packaged food)
      // and if the event has the message display enabled
      if (isPackagedFood && !wasPackagedFood && event?.packagedFoodVendorMessageDisplay && event?.packagedFoodVendorMessage) {
        const message = event.packagedFoodVendorMessage;
        const acknowledged = window.confirm(`${message}\n\nClick OK to acknowledge and continue, or Cancel to go back.`);
        
        if (!acknowledged) {
          // User cancelled, don't update to Packaged Food
          return;
        } else {
          // User acknowledged, set the flag
          setPackagedFoodAck(true);
        }
      }
    }
    
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const priceFor = (levelKey, position) => {
    if (!layout?.customLevels || !levelKey || !position) return 0;
    const l = layout.customLevels[levelKey];
    if (!l) return 0;
    return position === 'CORNER' ? Number(l.cornerPrice || 0) : Number(l.inlinePrice || 0);
  };
  const total = useMemo(() => rows.reduce((sum, r) => sum + priceFor(r.level, r.position), 0), [rows, layout]);
  const canEdit = !latest || latest.status === 'Pending';
  
  // Check if any booth has "Packaged Food" item type and if event requires acknowledgment
  const hasPackagedFood = useMemo(() => {
    return rows.some(r => (r.itemType || '').trim() === 'Packaged Food');
  }, [rows]);
  
  const showPackagedFoodAck = event?.packagedFoodVendorMessageDisplay && hasPackagedFood;
  
  // Reset packaged food acknowledgment if no packaged food booths remain
  useEffect(() => {
    if (!hasPackagedFood && packagedFoodAck) {
      setPackagedFoodAck(false);
    }
  }, [hasPackagedFood, packagedFoodAck]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) { setError('You must be logged in.'); return; }
    if (!ack) { setError('Please acknowledge the statement at the end.'); return; }
    if (showPackagedFoodAck && !packagedFoodAck) { 
      setError('Please acknowledge the packaged food vendor requirements.'); 
      return; 
    }
    // validate rows
    if (!rows.length) { setError('Please add at least one intended booth.'); return; }
    if (!selectedVendorId) { setError('Please select a vendor.'); return; }
    for (const r of rows) {
      if (!r.level || !r.position || !r.itemType) {
        setError('Each booth must specify level, position, and item type.');
        return;
      }
      if ((r.itemType || '').toLowerCase() === 'other' && !(r.notes || '').trim()) {
        setError('Please describe the items you will sell when selecting Other.');
        return;
      }
    }
    setError('');
    setSaving(true);
    try {
      const vendorId = selectedVendorId || null;
      if (!event || !layout) { setError('Event or layout missing. Start from an event registration link.'); return; }
      // Store estimates in cents (consistent with layout.level prices)
      const withEstimates = rows.map(r => ({ ...r, estimate: (priceFor(r.level, r.position) || 0) }));
      // If there's an existing pending application for this event, update it; else create new
      if (latest && latest.status === 'Pending') {
        // If the current user is a vendor member/admin for the selected vendor, use the vendor-based updater
        const selectedVendor = vendors.find(v => v.id === selectedVendorId);
        const isVendorMember = selectedVendor && Array.isArray(selectedVendor.memberUids) && currentUser && selectedVendor.memberUids.includes(currentUser.uid);

        if (isVendorMember && !(userType === 'Admin' || userType === 'Organizer')) {
          // vendor-admin user path: update by vendorId/eventId
          await updatePendingApplicationForVendor(selectedVendorId, event.id, { boothsRequested: withEstimates, acknowledgement: ack });
        } else {
          // owner or admin/organizer path: update by application id
          const updatePayload = { boothsRequested: withEstimates, acknowledgement: ack };
          if (userType === 'Admin' || userType === 'Organizer') updatePayload.vendorId = vendorId;
          await updateMyPendingApplication(latest.id, updatePayload);
        }
      } else {
        await createBoothApplication({ vendorId, eventId: event.id, layoutId: layout.id, boothsRequested: withEstimates, acknowledgement: ack });
      }
      // Show submission confirmation dialog instead of immediate navigation
      setShowSubmittedDialog(true);
    } catch (e1) {
      setError(e1?.message || 'Failed to submit application.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-container profile-wide-container vendor-application">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
      </div>
      <h2 className="profile-title">Vendor Booth Application</h2>
      {!event && (
        <div className="profile-error" style={{marginBottom:16}}>
          Missing event context. Please navigate here using the Apply button for the event.
        </div>
      )}
      {event && (
        <div className="user-card event-info-card combined-event-pricing">
          <div className="event-header-row">
            <div className="event-title">{event.name}</div>
            {event.linkToEventSite && (
              <a className="new-layout-btn" href={event.linkToEventSite.startsWith('http') ? event.linkToEventSite : `https://${event.linkToEventSite}`} target="_blank" rel="noreferrer">Event Site</a>
            )}
          </div>
          <div className="event-details-grid">
            <div className="event-detail"><span className="label">📅 Dates</span><span className="value">{[event.startDate, event.endDate].filter(Boolean).join(' - ') || '-'}</span></div>
            <div className="event-detail"><span className="label">📍 Location</span><span className="value">{event.location || '-'}</span></div>
            {layout?.name && (<div className="event-detail"><span className="label">🗂️ Layout</span><span className="value">{layout.name}</span></div>)}
          </div>
          {layout && (
            <div className="embedded-pricing">
              <div className="pricing-header-row">
                <div className="pricing-title">Booth Pricing</div>
                <div className="pricing-note">Per booth (Inline vs Corner)</div>
              </div>
              <div className="table-scroll">
                <table className="pricing-table compact-pricing">
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th>Inline</th>
                      <th>Corner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLevels
                      .filter(([key, l]) => !String(l?.name || key).toLowerCase().includes('invisible'))
                      .map(([key, l]) => (
                        <tr key={key}>
                          <td>{l.name || key}</td>
                          <td>{fmtCurrency(l.inlinePrice)}</td>
                          <td>{fmtCurrency(l.cornerPrice)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {/* How it works explanation */}
      <div className="user-card how-it-works">
        <div className="card-title">How it works</div>
        <div className="how-text">
          This is not the booth purchase process. You are applying to become an approved vendor for this event. After your application is reviewed and approved, you will be able to purchase booths in the system. This step does not reserve a specific booth. You can update your request until the provided deadline. After that, your application cannot be changed.
        </div>
      </div>
      {latest && latest.status && (
        <div className="profile-hint" style={{marginBottom: 10}}>
          Application Status: <span className={`status-badge status-${String(latest.status || '').toLowerCase()}`}>{latest.status}</span>{latest.reviewNote ? ` — ${latest.reviewNote}` : ''}
        </div>
      )}
      {latest?.status === 'Rejected' && (
        <div className="profile-error" style={{ marginBottom: 12, whiteSpace: 'pre-wrap' }}>
          {String(event?.rejectionMessage || '').trim() || 'We regret to inform you that your application as a vendor has been rejected and can no longer be modified. Please reach out to the Event Admin at bazaar@conv.icna.org for questions.'}
        </div>
      )}
      {/* Vendor selection card */}
      <div className="user-card" style={{ marginBottom: 12 }}>
        <label className="profile-label" style={{ fontWeight: 600, marginBottom: 6 }}>Vendor</label>
        <select
          className="profile-input"
          value={selectedVendorId}
          onChange={e => setSelectedVendorId(e.target.value)}
          disabled={!canEdit || !(userType === 'Admin' || userType === 'Organizer')}
        >
          <option value="">Select vendor…</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name || v.businessName || v.id}</option>
          ))}
        </select>
        {(!selectedVendorId && vendors.length === 0) && (
          <div className="profile-hint" style={{ marginTop: 6 }}>No vendors found for your account.</div>
        )}
        {(!(userType === 'Admin' || userType === 'Organizer') && vendors.length > 1) && (
          <div className="profile-hint" style={{ marginTop: 6 }}>Contact an organizer if this is the wrong vendor.</div>
        )}
      </div>
      {error && <div className="profile-error">{error}</div>}
      {rows.length === 0 && (
        <div className="profile-hint" style={{ marginBottom: 8 }}>
          No booths entered yet for this vendor.
        </div>
      )}
      <div className="table-scroll">
        <table className="booth-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Booth Level</th>
              <th>Position</th>
              <th>Item Type</th>
              <th>Est. Price</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{i+1}</td>
                <td>
                  <select className="profile-input" value={r.level} onChange={e => updateRow(i, { level: e.target.value })} disabled={!canEdit}>
                    <option value="">Select…</option>
                    {sortedLevels
                      .filter(([key, l]) => !String(l?.name || key).toLowerCase().includes('invisible'))
                      .map(([key, l]) => (
                      <option key={key} value={key}>
                        {(l.name || key)} — Inline {fmtCurrency(l.inlinePrice)} / Corner {fmtCurrency(l.cornerPrice)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select className="profile-input" value={r.position} onChange={e => updateRow(i, { position: e.target.value })} disabled={!canEdit}>
                    <option value="INLINE">Inline</option>
                    <option value="CORNER">Corner</option>
                  </select>
                </td>
                <td>
                  <select className="profile-input" value={r.itemType} onChange={e => updateRow(i, { itemType: e.target.value })} disabled={!canEdit}>
                    <option value="">Select…</option>
                    {itemTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {String(r.itemType || '').toLowerCase() === 'other' && (
                    <input
                      className="profile-input"
                      type="text"
                      placeholder="Describe items you will sell"
                      value={r.notes || ''}
                      onChange={e => updateRow(i, { notes: e.target.value })}
                      disabled={!canEdit}
                      style={{ marginTop: 6, minWidth: 220 }}
                    />
                  )}
                </td>
                <td>{fmtCurrency(priceFor(r.level, r.position))}</td>
                <td>
                  {rows.length > 1 && canEdit && (
                    <button type="button" className="danger-light-btn" onClick={() => removeRow(i)}>Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="actions-row">
        {(!latest || latest.status === 'Pending') && (
          <button type="button" className="apply-btn" onClick={addRow}>Add Another Booth</button>
        )}
        <div className="total-est"><span className="label">Total Est.</span> <span className="value">{fmtCurrency(total)}</span></div>
      </div>
      {(!latest || latest.status === 'Pending') && (
        <div className="profile-actions">
          <label className="profile-label" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontWeight: 500, marginBottom: 10 }}>
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} style={{ marginTop: 4 }} />
            <span>
              {event?.applicationAckMessage || 'I acknowledge that this is an application to purchase booths for the event. My purchase ability will be enabled only after an admin reviews and approves my application.'}
            </span>
          </label>
          {showPackagedFoodAck && (
            <label className="profile-label" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontWeight: 500, marginBottom: 10, marginTop: 16, padding: 12, backgroundColor: '#e8f5e9', border: '1px solid #4caf50', borderRadius: 4 }}>
              <input type="checkbox" checked={packagedFoodAck} onChange={e => setPackagedFoodAck(e.target.checked)} style={{ marginTop: 4 }} disabled />
              <span>
                ✓ I have acknowledged the packaged food vendor requirements
              </span>
            </label>
          )}
        </div>
      )}
      <div className="profile-actions" style={{marginTop: 10}}>
        <button type="button" className="cancel-btn" onClick={() => navigate(-1)} disabled={saving}>Cancel</button>
        {(!latest || latest.status === 'Pending') && (
          <button 
            type="button" 
            className="apply-btn" 
            onClick={handleSubmit} 
            disabled={saving || !ack || (showPackagedFoodAck && !packagedFoodAck)}
          >
            {saving ? 'Saving…' : (latest ? 'Update Application' : 'Submit Application')}
          </button>
        )}
      </div>
      {showSubmittedDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div style={{ background: '#fff', padding: 24, maxWidth: 600, width: '90%', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0 }}>Application Submitted</h3>
            <p>Your vendor application for <strong>{event?.name || 'this event'}</strong> has been submitted and is now under review.</p>
            <p style={{ marginTop: 8 }}>You can modify your application for this event until it is approved — simply return to the home page and click the Update Application button. You will receive an email notification once your application is approved.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="cancel-btn" onClick={() => { setShowSubmittedDialog(false); navigate(-1); }}>Back</button>
              <button type="button" className="apply-btn" onClick={() => { setShowSubmittedDialog(false); navigate('/'); }}>Go to Home</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorApplication;
